/**
 * Calmy product-page conversion components.
 *
 *   1  the offer countdown strip
 *   2  the community signup dialog
 *   3  "Read more" on a long featured review
 *
 * All three are independent: a page carrying one of them and not the others
 * runs only that part, and a block removed in the theme editor leaves nothing
 * behind to run.
 *
 * NOTHING HERE TOUCHES THE PRODUCT FORM. No listener is attached to it, no
 * input inside it is read or written, and the signup lives in a <dialog>
 * rendered outside it - so the bundles app keeps the whole, unmodified form it
 * injects its selection into.
 */
(function () {
  "use strict";

  /* THE COUNTDOWN IS NOT HERE. assets/calmy-countdown.js already owns every
     [data-calmy-countdown] on the page, with one interval for all of them, and
     snippets/calmy-countdown.liquid is written to its attributes. A second
     implementation binding the same nodes is the bug that had two galleries
     fighting over one set of tiles, so there is deliberately none. */

  /* ═══ 1. COMMUNITY SIGNUP ══════════════════════════════════════════════════
   *
   * The card's action opens a native <dialog> holding the theme's existing
   * Shopify customer form - the same {% form 'customer' %} the newsletter uses,
   * so a signup here creates the same customer, with the same tag, in the same
   * place. Nothing new is integrated and nothing is faked.
   *
   * If the form has just posted, Liquid marks the dialog and it is opened on
   * load so the customer sees the real result rather than a silent page.
   */
  function initCommunity() {
    var dlg = document.querySelector("[data-calmy-community]");
    if (!dlg || dlg.__calmyComm) return;
    dlg.__calmyComm = true;

    function open() {
      if (dlg.showModal) { try { dlg.showModal(); return; } catch (e) { /* already open */ } }
      dlg.setAttribute("open", "");
    }
    function close() {
      if (dlg.close) { try { dlg.close(); return; } catch (e) { /* not open */ } }
      dlg.removeAttribute("open");
    }

    document.querySelectorAll("[data-calmy-community-open]").forEach(function (b) {
      if (b.__calmyComm) return;
      b.__calmyComm = true;
      b.addEventListener("click", function (e) { e.preventDefault(); open(); });
    });
    dlg.querySelectorAll("[data-calmy-community-close]").forEach(function (b) {
      b.addEventListener("click", function () { close(); });
    });
    /* The backdrop is the dialog itself; a click on the panel is not one. */
    dlg.addEventListener("click", function (e) { if (e.target === dlg) close(); });

    /* Liquid marks the dialog when Shopify's customer form has just posted;
       reopening it is how the customer sees the real success or error instead
       of a reload that looks like nothing happened. */
    if (dlg.querySelector("[data-calmy-community-posted]")) open();
  }

  /* ═══ 2. REVIEW "READ MORE" ════════════════════════════════════════════════
   *
   * Measured, not assumed. The button is only added when the quote is actually
   * taller than its clamp, so a short review never grows a control that would
   * reveal nothing.
   */
  function initReviewMore() {
    document.querySelectorAll("[data-calmy-review-clamp]").forEach(function (q) {
      if (q.__calmyMore) return;
      q.__calmyMore = true;

      q.classList.add("is-clamped");
      /* scrollHeight vs clientHeight, read after the clamp is applied. A few
         pixels of slack keeps sub-pixel line heights from producing a button
         that expands the text by nothing. */
      if (q.scrollHeight - q.clientHeight < 6) {
        q.classList.remove("is-clamped");
        return;
      }

      var more = document.createElement("button");
      more.type = "button";
      more.className = "cfrev__more";
      var labelMore = q.getAttribute("data-more") || "Read more";
      var labelLess = q.getAttribute("data-less") || "Read less";
      more.textContent = labelMore;
      more.setAttribute("aria-expanded", "false");
      if (q.id) more.setAttribute("aria-controls", q.id);

      more.addEventListener("click", function () {
        var open = q.classList.toggle("is-clamped") === false;
        more.textContent = open ? labelLess : labelMore;
        more.setAttribute("aria-expanded", open ? "true" : "false");
      });
      q.insertAdjacentElement("afterend", more);
    });
  }

  /* ═══ 3. THE RATING ROW'S LINK ═════════════════════════════════════════════
   *
   * The row links to #reviews, but that section renders only once a review has
   * been written - so on a product with none the anchor pointed at an id that
   * was not on the page. Rather than leave a link that silently does nothing,
   * the anchor is replaced by a plain span: the row looks identical and simply
   * is not clickable.
   *
   * Only anchors marked data-calmy-ratelink are considered. A row given an
   * explicit destination in the editor carries no such attribute and is never
   * touched.
   */
  function checkRateLink(a) {
    if (a.__cebRate) return;
    a.__cebRate = true;
    var href = a.getAttribute("href") || "";
    if (href.charAt(0) !== "#") return;
    if (document.getElementById(href.slice(1))) return;

    var span = document.createElement("span");
    span.className = a.className.replace(/\bcinfo__ratinglink\b/, "").trim();
    span.setAttribute("style", a.getAttribute("style") || "");
    while (a.firstChild) span.appendChild(a.firstChild);
    a.parentNode.replaceChild(span, a);
  }

  /* ═══ 4. THE COUNTDOWN'S PLACE IN THE BUNDLES WIDGET ═══════════════════════
   *
   * The wanted order is the app's own heading, then the countdown, then the
   * offer cards. The heading and the cards are both rendered by Kaching inside
   * its app block, so Liquid cannot put anything between them - the theme's
   * markup stops at the outside of that block.
   *
   * So the strip is rendered by the theme in its own block position and moved
   * once, to sit directly after the app's title element. What that costs is
   * one DOM move; what it avoids is recreating the widget, duplicating the
   * heading, or reading text to find the right node.
   *
   * IT MATCHES ON THE APP'S CLASS, NOT ON ITS WORDS. .kaching-bundles__block-title
   * is the app's own hook, so renaming "BUY MORE & SAVE" - or translating it -
   * changes nothing here. Text matching would have broken on the first edit.
   *
   * IT IS IDEMPOTENT. The observer only acts when the strip is not already the
   * title's next sibling, so Kaching re-rendering its bars on a variant change
   * puts it back exactly once and a settled DOM costs nothing.
   */
  function placeCountdown() {
    var strip = document.querySelector("[data-calmy-countdown]");
    if (!strip) return;
    /* THE HEADING TRAVELS WITH THE BAR. The snippet now wraps the strip in a
       .cdeal-wrap carrying the campaign heading; moving the strip alone left
       the heading behind in the buy column, under nothing. Move the wrapper
       when there is one, and fall back to the bare strip for any older markup
       that has none. */
    var unit = (strip.closest && strip.closest(".cdeal-wrap")) || strip;
    var block = document.querySelector(".kaching-bundles__block");
    if (!block) return;
    var title = block.querySelector(".kaching-bundles__block-title");
    if (!title) return;
    if (title.nextElementSibling === unit) return;
    title.insertAdjacentElement("afterend", unit);
    strip.classList.add("cdeal--inwidget");
    if (unit !== strip) unit.classList.add("cdeal-wrap--inwidget");
  }

  function watchCountdown() {
    if (window.__calmyDealPlaced) return;
    var block = document.querySelector(".kaching-bundles__block");
    if (!block) return;
    window.__calmyDealPlaced = true;
    placeCountdown();
    if (window.MutationObserver) {
      new MutationObserver(placeCountdown).observe(block, { childList: true });
    }
  }

  function boot(scope) {
    var root = scope || document;
    root.querySelectorAll("a[data-calmy-ratelink]").forEach(checkRateLink);
    initCommunity();
    initReviewMore();
    /* The app block arrives after its script runs, so this is retried for a
       few seconds and then gives up rather than polling forever. */
    watchCountdown();
    var tries = 0;
    var t = window.setInterval(function () {
      watchCountdown();
      if (++tries > 40 || window.__calmyDealPlaced) window.clearInterval(t);
    }, 250);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(); });
  } else {
    boot();
  }
  /* The theme editor replaces a section's markup wholesale when a setting
     changes; every init above is flag-guarded, so re-running over live nodes
     is a no-op and only the new ones are wired. */
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
